import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';

import { db } from '../firebase/config';
import { sendEmailTemplate } from '../utils/emailUtils';

interface TripInvitation {
   tripId: string;
   tripTitle: string;
   inviterId: string;
   inviterName: string;
   email: string;
   role: 'editor' | 'owner';
   customMessage?: string;
   expiresAt: Date;
}

/**
 * Send a trip invitation to a user by email
 */
export const sendTripInvitation = async (invitation: TripInvitation): Promise<string> => {
   try {
      const { getAuthHeaders } = await import('../utils/auth');
      const headers = await getAuthHeaders();

      const response = await fetch('/api/trip/send-invite', {
         method: 'POST',
         headers,
         body: JSON.stringify({
            to: invitation.email,
            subject: `You're invited to join ${invitation.tripTitle}`,
            inviterName: invitation.inviterName,
            tripTitle: invitation.tripTitle,
            tripId: invitation.tripId,
            message: invitation.customMessage,
            recipient_name: '',
         }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to send invitation');
      }

      const result = await response.json();
      return result.invitationId || 'invitation-sent';
   } catch (error) {
      console.error('Error sending trip invitation:', error);
      throw error;
   }
};

/**
 * Resend a trip invitation
 */
export const resendTripInvitation = async (invitationId: string): Promise<void> => {
   try {
      const invitationRef = doc(db, 'tripInvitations', invitationId);

      // Update the expiration date and set updatedAt timestamp
      await updateDoc(invitationRef, {
         expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
         updatedAt: serverTimestamp(),
      });

      // In a real app, you'd send the email again here
   } catch (error) {
      console.error('Error resending invitation:', error);
      throw new Error('Failed to resend invitation');
   }
};

// Service for managing invitations
export interface CancelInvitationRequest {
   email: string;
}

export interface CancelInvitationResponse {
   success: boolean;
   message: string;
   details: {
      deletedInvitations: Array<{ id: string; status: string }>;
      removedFromParticipants: boolean;
   };
}

/**
 * Cancel a trip invitation
 */
export const cancelTripInvitation = async (
   tripId: string,
   email: string
): Promise<CancelInvitationResponse> => {
   const { getAuthHeaders } = await import('../utils/auth');
   const headers = await getAuthHeaders();

   const response = await fetch(`/api/trip/${tripId}/cancel-invite`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
   });

   if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to cancel trip invitation');
   }

   return response.json();
};

/**
 * Cancel an event invitation
 */
export const cancelEventInvitation = async (
   eventId: string,
   email: string
): Promise<CancelInvitationResponse> => {
   const { getAuthHeaders } = await import('../utils/auth');
   const headers = await getAuthHeaders();

   const response = await fetch(`/api/event/${eventId}/cancel-invite`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
   });

   if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to cancel event invitation');
   }

   return response.json();
};

/**
 * Accept a trip invitation
 */
export const acceptTripInvitation = async (
  invitationId: string, 
  userId: string
): Promise<void> => {
  try {
    const invitationRef = doc(db, 'tripInvitations', invitationId);
    
    // Update invitation status
    await updateDoc(invitationRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      acceptedBy: userId
    });
    
    // In a real implementation, you would also add the user to the trip participants collection
    
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw new Error('Failed to accept invitation');
  }
};

/**
 * Decline a trip invitation
 */
export const declineTripInvitation = async (invitationId: string): Promise<void> => {
  try {
    const invitationRef = doc(db, 'tripInvitations', invitationId);
    
    // Update invitation status
    await updateDoc(invitationRef, {
      status: 'declined',
      declinedAt: serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error declining invitation:', error);
    throw new Error('Failed to decline invitation');
  }
};

/**
 * Get all trips a user has been invited to
 */
export const getUserInvitations = async (userEmail: string): Promise<any[]> => {
  try {
    const invitationsQuery = query(
      collection(db, 'tripInvitations'),
      where('email', '==', userEmail.toLowerCase()),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(invitationsQuery);
    
    const invitations: any[] = [];
    snapshot.forEach(doc => {
      invitations.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return invitations;
  } catch (error) {
    console.error('Error fetching user invitations:', error);
    throw new Error('Failed to load invitations');
  }
}; 